import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [cidade, setCidade] = useState('');
  const [cidades, setCidades] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCidades, setLoadingCidades] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    carregarCidades();
  }, []);

  const carregarCidades = useCallback(async () => {
    try {
      setError('');
      const response = await api.get('/cidades');
      
      if (response.data && response.data.success) {
        const cidadesData = response.data.data || [];
        setCidades(cidadesData);
        if (cidadesData.length > 0) {
          setCidade(cidadesData[0].id_cidade.toString());
        } else {
          // Se não houver cidades, mostrar mensagem
          setError('Nenhuma cidade cadastrada. Entre em contato com o administrador para cadastrar uma cidade.');
        }
      } else {
        setCidades([]);
        setError('Erro ao carregar cidades. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao carregar cidades:', err);
      setCidades([]);
      
      // Verificar tipo de erro
      if (err.code === 'ECONNREFUSED' || err.message?.includes('Network')) {
        setError('Não foi possível conectar ao servidor. Verifique sua conexão.');
      } else if (err.response?.status === 503) {
        // Erro de conexão com banco de dados
        const errorMsg = err.response?.data?.message || 'Erro de conexão com banco de dados';
        setError(`${errorMsg}. Configure a senha do PostgreSQL no arquivo backend/.env`);
      } else if (err.response?.data?.success === false) {
        // API retornou erro estruturado
        setError(err.response.data.message || 'Erro ao carregar cidades. Verifique se o banco de dados está configurado corretamente.');
      } else {
        setError('Erro ao carregar cidades. Verifique se o banco de dados está configurado corretamente.');
      }
    } finally {
      setLoadingCidades(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !senha || !cidade) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    setLoading(true);

    try {
      const id_cidade = parseInt(cidade);
      const result = await login(email, senha, id_cidade);

      if (result.success) {
        navigate('/');
      } else {
        setError(result.message || 'Erro ao fazer login. Verifique suas credenciais.');
      }
    } catch (err) {
      console.error('Erro no handleSubmit:', err);
      setError('Erro ao processar login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-wrapper">
        <div className="login-form-section">
          <div className="login-form-header">
            <h2 className="login-title">
              <svg className="login-title-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7 3L13 10L7 17" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Login
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="login-alert login-alert-error">
                <span>{error}</span>
              </div>
            )}

            {!loadingCidades && cidades.length === 0 && !error && (
              <div className="login-alert login-alert-warning">
                <span>Nenhuma cidade cadastrada. Entre em contato com o administrador.</span>
              </div>
            )}

            <div className="login-field">
              <div className="login-input-wrapper login-input-id">
                <span className="login-input-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <rect x="3" y="5" width="14" height="10" stroke="currentColor" strokeWidth="1.5"/>
                    <rect x="6" y="8" width="8" height="4" stroke="currentColor" strokeWidth="1.5"/>
                    <line x1="8" y1="11" x2="12" y2="11" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </span>
                <input
                  type="text"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-input"
                  required
                  placeholder="29627022870"
                  disabled={loading}
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="login-field">
              <div className="login-input-wrapper login-input-password">
                <span className="login-input-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <rect x="3" y="8" width="14" height="9" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="10" cy="12.5" r="1.5" fill="currentColor"/>
                    <path d="M5 8V5.5C5 3.567 6.567 2 8.5 2H11.5C13.433 2 15 3.567 15 5.5V8" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="senha"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="login-input"
                  required
                  placeholder="•••••••"
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
            </div>

            <div className="login-field">
              <div className="login-input-wrapper login-input-entity">
                <select
                  id="cidade"
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value)}
                  className="login-input login-select"
                  disabled={loadingCidades || loading}
                  required
                >
                  <option value="">
                    {loadingCidades ? 'Carregando cidades...' : 'Escolha uma entidade...'}
                  </option>
                  {cidades.length > 0 ? (
                    cidades.map((c) => (
                      <option key={c.id_cidade} value={c.id_cidade}>
                        {c.sigla || c.descricao}
                      </option>
                    ))
                  ) : (
                    !loadingCidades && <option value="" disabled>Nenhuma cidade cadastrada</option>
                  )}
                </select>
                <span className="login-select-arrow">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </div>
            </div>

            <div className="login-buttons">
              <button 
                type="submit" 
                className="login-button login-button-primary" 
                disabled={loading || loadingCidades || !cidade || !email || !senha}
              >
                {loading ? (
                  <>
                    <svg className="login-spinner" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="31.416">
                        <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416;0 31.416" repeatCount="indefinite"/>
                        <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416;-31.416" repeatCount="indefinite"/>
                      </circle>
                    </svg>
                    <span>Entrando...</span>
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M3 10L5 8M5 8L3 6M5 8H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Entrar</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
